import { api } from '@/lib/api';
import type {
  PaginatedOpportunities,
  OpportunityListFilters,
  Opportunity,
  OpportunityStats,
  PipelineResponse,
} from '../types/internal-opportunity.types';
import type { PaginationParams } from '../types/opportunity.types';

const BASE_URL = '/tm/opportunities';

/**
 * Service pour les opportunités internes (importées)
 */
export const opportunitiesService = {
  /**
   * Récupère la liste paginée des opportunités avec filtres
   */
  async getAll(
    filters: OpportunityListFilters = {},
    pagination: Partial<PaginationParams> = {}
  ): Promise<PaginatedOpportunities> {
    const params = new URLSearchParams();

    // Pagination
    params.set('page', String(pagination.page || 1));
    params.set('pageSize', String(pagination.pageSize || 20));
    if (pagination.sortBy) params.set('sortBy', pagination.sortBy);
    if (pagination.sortOrder) params.set('sortOrder', pagination.sortOrder);

    // Filtres
    if (filters.search) params.set('search', filters.search);
    if (filters.status?.length) params.set('status', filters.status.join(','));
    if (filters.decision) params.set('decision', filters.decision);
    if (filters.buyerId) params.set('buyerId', String(filters.buyerId));
    if (filters.deadlineFrom) params.set('deadlineFrom', filters.deadlineFrom);
    if (filters.deadlineTo) params.set('deadlineTo', filters.deadlineTo);
    if (filters.minAmount) params.set('minAmount', String(filters.minAmount));
    if (filters.maxAmount) params.set('maxAmount', String(filters.maxAmount));
    if (filters.marketType) params.set('marketType', filters.marketType);
    if (filters.importSource) params.set('importSource', filters.importSource);
    if (filters.assignedToMe) params.set('assignedToMe', 'true');

    const response = await api.get<PaginatedOpportunities>(
      `${BASE_URL}?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Récupère une opportunité par son ID
   */
  async getById(id: number): Promise<Opportunity> {
    const response = await api.get<Opportunity>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Récupère les statistiques des opportunités
   */
  async getStats(): Promise<OpportunityStats> {
    const response = await api.get<OpportunityStats>(`${BASE_URL}/stats`);
    return response.data;
  },

  /**
   * Met à jour une opportunité
   */
  async update(id: number, data: Partial<Opportunity>): Promise<Opportunity> {
    const response = await api.patch<Opportunity>(
      `${BASE_URL}/${id}`,
      data
    );
    return response.data;
  },

  /**
   * Met à jour le statut d'une opportunité
   */
  async updateStatus(id: number, status: string): Promise<Opportunity> {
    const response = await api.patch<Opportunity>(
      `${BASE_URL}/${id}/status`,
      { status }
    );
    return response.data;
  },

  /**
   * Enregistre une décision GO/NO-GO
   */
  async makeDecision(
    id: number,
    decision: 'go' | 'no_go',
    reason?: string
  ): Promise<Opportunity> {
    const response = await api.patch<Opportunity>(
      `${BASE_URL}/${id}/decision`,
      { decision, reason }
    );
    return response.data;
  },

  /**
   * Supprime une opportunité (soft delete)
   */
  async delete(id: number): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Récupère le pipeline Kanban des opportunités
   */
  async getPipeline(filters: Partial<OpportunityListFilters> = {}): Promise<PipelineResponse> {
    const params = new URLSearchParams();
    if (filters.buyerId) params.set('buyerId', String(filters.buyerId));
    if (filters.assignedToMe) params.set('assignedToMe', 'true');

    const queryString = params.toString();
    const url = queryString ? `${BASE_URL}/pipeline?${queryString}` : `${BASE_URL}/pipeline`;
    const response = await api.get<PipelineResponse>(url);
    return response.data;
  },

  /**
   * Rafraîchit les données d'une opportunité depuis sa source externe (BOAMP)
   */
  async refreshFromSource(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      `/tm/import/refresh/${id}`
    );
    return response.data;
  },
};
